/**
 * Calculate portfolio rebalancing
 * @param {Array} positions - Array of position objects with ticker, amount, and targetPercent
 * @param {string} mode - Rebalancing mode: 'standard', 'add-only', 'sell-only', 'contribution', 'withdrawal'
 * @param {number} modeAmount - Amount for contribution or withdrawal modes
 * @returns {Object} Rebalancing data with current and target allocations
 */
export function calculateRebalancing(positions, mode = 'standard', modeAmount = 0) {
  // Filter out invalid positions
  const validPositions = positions.filter((pos) => {
    const amount = parseFloat(pos.amount);
    const targetPercent = parseFloat(pos.targetPercent);
    return !isNaN(amount) && amount >= 0 && !isNaN(targetPercent) && targetPercent >= 0;
  });

  if (validPositions.length === 0) {
    return { positions: [], totalValue: 0 };
  }

  // Use only valid positions going forward
  positions = validPositions;

  // Calculate total portfolio value
  const totalValue = positions.reduce((sum, pos) => sum + parseFloat(pos.amount), 0);

  // Handle contribution mode
  if (mode === 'contribution' && modeAmount > 0) {
    return calculateContribution(positions, totalValue, modeAmount);
  }

  // Handle withdrawal mode
  if (mode === 'withdrawal' && modeAmount > 0) {
    if (modeAmount >= totalValue) {
      throw new Error('Withdrawal amount cannot exceed total portfolio value');
    }
    return calculateWithdrawal(positions, totalValue, modeAmount);
  }

  // Calculate current allocations and rebalancing actions
  const positionsWithCalcs = positions.map((pos) => {
    const currentAmount = parseFloat(pos.amount);
    const currentPercent = totalValue > 0 ? (currentAmount / totalValue) * 100 : 0;
    const targetPercent = parseFloat(pos.targetPercent);
    const targetAmount = (targetPercent / 100) * totalValue;
    const difference = targetAmount - currentAmount;

    let action = difference > 0 ? 'BUY' : difference < 0 ? 'SELL' : 'HOLD';
    let displayDifference = difference;

    // Apply mode-specific logic
    if (mode === 'add-only') {
      // Only show positions that need buying
      if (difference <= 0) {
        action = 'HOLD';
        displayDifference = 0;
      }
    } else if (mode === 'sell-only') {
      // Only show positions that need selling
      if (difference >= 0) {
        action = 'HOLD';
        displayDifference = 0;
      }
    }

    return {
      ticker: pos.ticker,
      currentAmount: currentAmount,
      currentPercent: currentPercent,
      targetPercent: targetPercent,
      targetAmount: targetAmount,
      difference: displayDifference,
      action: action,
      costBasis: pos.costBasis ?? null,
      purchaseDate: pos.purchaseDate ?? null,
    };
  });

  // Calculate mode-specific totals
  let modeData = {};
  if (mode === 'add-only') {
    const totalToAdd = positionsWithCalcs.reduce((sum, pos) => {
      return sum + (pos.difference > 0 ? pos.difference : 0);
    }, 0);
    modeData = {
      totalToAdd,
      newTotalValue: totalValue + totalToAdd,
    };
  } else if (mode === 'sell-only') {
    const totalToSell = positionsWithCalcs.reduce((sum, pos) => {
      return sum + (pos.difference < 0 ? Math.abs(pos.difference) : 0);
    }, 0);
    modeData = {
      totalToSell,
      newTotalValue: totalValue - totalToSell,
    };
  }

  return {
    totalValue,
    positions: positionsWithCalcs,
    mode,
    modeData,
  };
}

// Fallback when the user hasn't entered a cost basis: assume basis is 80% of
// the sale amount (i.e. a 20% gain), the app's original flat assumption.
export const ASSUMED_COST_BASIS_RATIO = 0.8;
// Flat rate applied to net gains. Real bracket/short-term rates are out of
// scope until approved — purchase dates only classify the holding period.
export const FLAT_CAPITAL_GAINS_RATE = 0.15;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Estimate the tax impact of the SELL actions in a rebalancing result.
 *
 * Positions with a user-entered cost basis use the real gain, prorated for
 * partial sales: selling a fraction of a position realizes that same fraction
 * of its total basis, so gain = sellAmount * (1 - costBasis / currentAmount).
 * Positions without one fall back to the assumed 80% basis and are flagged
 * `hasRealBasis: false` so the UI can mark them as estimated.
 *
 * @param {Array} positions - Positions from calculateRebalancing (with action,
 *   difference, currentAmount, and optional costBasis/purchaseDate)
 * @param {Date} [today] - Injectable for testing; defaults to now
 * @returns {Object} { sellEstimates, totalCapitalGains, estimatedTaxes,
 *   hasAssumedPositions } — sellEstimates entries carry { ticker, sellAmount,
 *   gain, hasRealBasis, holdingPeriod: 'long'|'short'|null }
 */
export function estimateTaxImpact(positions, today = new Date()) {
  const sellEstimates = positions
    .filter((p) => p.action === 'SELL')
    .map((p) => {
      const sellAmount = Math.abs(p.difference);
      const costBasis = parseFloat(p.costBasis);
      const currentAmount = parseFloat(p.currentAmount);
      const hasRealBasis = !isNaN(costBasis) && costBasis > 0 && currentAmount > 0;

      const gain = hasRealBasis
        ? sellAmount * (1 - costBasis / currentAmount)
        : sellAmount * (1 - ASSUMED_COST_BASIS_RATIO);

      let holdingPeriod = null;
      if (p.purchaseDate) {
        const purchased = new Date(`${p.purchaseDate}T00:00:00`);
        if (!isNaN(purchased.getTime()) && purchased <= today) {
          holdingPeriod = (today - purchased) / MS_PER_DAY > 365 ? 'long' : 'short';
        }
      }

      return { ticker: p.ticker, sellAmount, gain, hasRealBasis, holdingPeriod };
    });

  const totalCapitalGains = sellEstimates.reduce((sum, e) => sum + e.gain, 0);
  // Real basis data can produce losses; losses offset gains and no tax is
  // owed on a net loss. The assumed-basis path always yields positive gains,
  // so legacy behavior is unchanged.
  const estimatedTaxes = Math.max(0, totalCapitalGains) * FLAT_CAPITAL_GAINS_RATE;
  const hasAssumedPositions = sellEstimates.some((e) => !e.hasRealBasis);

  return { sellEstimates, totalCapitalGains, estimatedTaxes, hasAssumedPositions };
}

/**
 * Format currency
 * @param {number} value - Number to format as currency
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value) {
  if (value == null || isNaN(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format percentage
 * @param {number} value - Number to format as percentage
 * @returns {string} Formatted percentage string
 */
export function formatPercent(value) {
  if (value == null || isNaN(value)) return '0.00%';
  return `${value.toFixed(2)}%`;
}

/**
 * Calculate contribution allocation
 * @param {Array} positions - Array of position objects
 * @param {number} currentTotal - Current portfolio value
 * @param {number} contribution - Amount to contribute
 * @returns {Object} Contribution allocation data
 */
function calculateContribution(positions, currentTotal, contribution) {
  const newTotal = currentTotal + contribution;

  const positionsWithCalcs = positions.map((pos) => {
    const currentAmount = parseFloat(pos.amount);
    const currentPercent = currentTotal > 0 ? (currentAmount / currentTotal) * 100 : 0;
    const targetPercent = parseFloat(pos.targetPercent);

    // Calculate target amount after contribution
    const newTargetAmount = (targetPercent / 100) * newTotal;
    const amountToAdd = newTargetAmount - currentAmount;

    // New allocation after contribution
    const newAmount = currentAmount + (amountToAdd > 0 ? amountToAdd : 0);
    const newPercent = (newAmount / newTotal) * 100;

    return {
      ticker: pos.ticker,
      currentAmount: currentAmount,
      currentPercent: currentPercent,
      targetPercent: targetPercent,
      targetAmount: newTargetAmount,
      difference: amountToAdd > 0 ? amountToAdd : 0,
      action: amountToAdd > 0 ? 'BUY' : 'HOLD',
      newAmount: newAmount,
      newPercent: newPercent,
      costBasis: pos.costBasis ?? null,
      purchaseDate: pos.purchaseDate ?? null,
    };
  });

  // Sum of the buys needed to fully close every underweight gap.
  const totalAllocated = positionsWithCalcs.reduce((sum, pos) => sum + pos.difference, 0);

  if (totalAllocated > contribution && totalAllocated > 0) {
    // Closing every gap would cost more than the contribution. Since we can't
    // sell in contribution mode, scale each buy down proportionally to its gap
    // so the recommended buys sum to exactly the contribution.
    const scale = contribution / totalAllocated;
    positionsWithCalcs.forEach((pos) => {
      pos.difference *= scale;
      pos.newAmount = pos.currentAmount + pos.difference;
      pos.newPercent = (pos.newAmount / newTotal) * 100;
      pos.action = pos.difference > 0 ? 'BUY' : 'HOLD';
    });
  } else if (totalAllocated < contribution) {
    // If allocated less than contribution, distribute remainder proportionally to targets
    const remainder = contribution - totalAllocated;
    const totalTargetPercent = positions.reduce(
      (sum, pos) => sum + parseFloat(pos.targetPercent),
      0
    );

    positionsWithCalcs.forEach((pos) => {
      const additionalAllocation = (pos.targetPercent / totalTargetPercent) * remainder;
      pos.difference += additionalAllocation;
      pos.newAmount += additionalAllocation;
      pos.newPercent = (pos.newAmount / newTotal) * 100;
      if (pos.difference > 0) {
        pos.action = 'BUY';
      }
    });
  }

  // Report what was actually allocated, not the raw contribution.
  const finalAllocated = positionsWithCalcs.reduce((sum, pos) => sum + pos.difference, 0);

  return {
    totalValue: currentTotal,
    positions: positionsWithCalcs,
    mode: 'contribution',
    modeData: {
      contributionAmount: contribution,
      newTotalValue: newTotal,
      totalAllocated: finalAllocated,
    },
  };
}

/**
 * Calculate withdrawal allocation
 * @param {Array} positions - Array of position objects
 * @param {number} currentTotal - Current portfolio value
 * @param {number} withdrawal - Amount to withdraw
 * @returns {Object} Withdrawal allocation data
 */
function calculateWithdrawal(positions, currentTotal, withdrawal) {
  const newTotal = currentTotal - withdrawal;

  const positionsWithCalcs = positions.map((pos) => {
    const currentAmount = parseFloat(pos.amount);
    const currentPercent = currentTotal > 0 ? (currentAmount / currentTotal) * 100 : 0;
    const targetPercent = parseFloat(pos.targetPercent);

    // Calculate target amount after withdrawal
    const newTargetAmount = (targetPercent / 100) * newTotal;
    const amountToSell = currentAmount - newTargetAmount;

    // New allocation after withdrawal
    const newAmount = currentAmount - (amountToSell > 0 ? amountToSell : 0);
    const newPercent = newTotal > 0 ? (newAmount / newTotal) * 100 : 0;

    return {
      ticker: pos.ticker,
      currentAmount: currentAmount,
      currentPercent: currentPercent,
      targetPercent: targetPercent,
      targetAmount: newTargetAmount,
      difference: amountToSell > 0 ? -amountToSell : 0,
      action: amountToSell > 0 ? 'SELL' : 'HOLD',
      newAmount: newAmount,
      newPercent: newPercent,
      costBasis: pos.costBasis ?? null,
      purchaseDate: pos.purchaseDate ?? null,
    };
  });

  // Sum of the sells needed to trim every overweight position to target.
  const totalSold = positionsWithCalcs.reduce((sum, pos) => sum + Math.abs(pos.difference), 0);

  if (totalSold > withdrawal && totalSold > 0) {
    // Trimming every overweight position would sell more than the withdrawal.
    // Since we can't buy in withdrawal mode, scale each sell down proportionally
    // so the recommended sells sum to exactly the withdrawal.
    const scale = withdrawal / totalSold;
    positionsWithCalcs.forEach((pos) => {
      pos.difference *= scale;
      pos.newAmount = pos.currentAmount + pos.difference;
      pos.newPercent = newTotal > 0 ? (pos.newAmount / newTotal) * 100 : 0;
      pos.action = pos.difference < 0 ? 'SELL' : 'HOLD';
    });
  } else if (totalSold < withdrawal) {
    // If sold less than withdrawal, distribute remainder proportionally
    const remainder = withdrawal - totalSold;
    const totalCurrentAmount = positions.reduce((sum, pos) => sum + parseFloat(pos.amount), 0);

    positionsWithCalcs.forEach((pos) => {
      const additionalSale =
        totalCurrentAmount > 0 ? (pos.currentAmount / totalCurrentAmount) * remainder : 0;
      pos.difference -= additionalSale;
      pos.newAmount -= additionalSale;
      pos.newPercent = newTotal > 0 ? (pos.newAmount / newTotal) * 100 : 0;
      if (pos.difference < 0) {
        pos.action = 'SELL';
      }
    });
  }

  // Report what was actually sold, not the raw withdrawal.
  const finalSold = positionsWithCalcs.reduce((sum, pos) => sum + Math.abs(pos.difference), 0);

  return {
    totalValue: currentTotal,
    positions: positionsWithCalcs,
    mode: 'withdrawal',
    modeData: {
      withdrawalAmount: withdrawal,
      newTotalValue: newTotal,
      totalSold: finalSold,
    },
  };
}
